import { type FormEvent, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Button, Card, ErrorNote, Field, Modal, PageHeader, Select, Spinner, useLoad } from "../components/ui"
import { GoogleDriveLogo } from "../components/logos"
import { api } from "../lib/api"

// Connections to a customer's own systems.
//
// Two surfaces share this page:
// - SAP/Dynatrace connections: company-wide, requirements-driven (the form is
//   built from /connections/requirements so a kind the backend cannot validate
//   says so here instead of appearing to have been checked).
// - Personal Google Drive connections (ROA-8): one employee's own Drive, added
//   below the company connections. The connect flow is a full-page redirect to
//   Google, which sends the browser back with ?googleDrive=… — handled the way
//   the Context Hub space page handles its own return.

type Requirement = {
  targetKind: string
  implemented: boolean
  required: string[]
  requiredSecrets: string[]
  fields: string[]
  either?: { describes: string; fields: string[] }[]
  note?: string
}

type Requirements = {
  platforms: string[]
  targetKinds: Requirement[]
  /** Which kinds fit which platform — drives the two selects. */
  kindsByPlatform: Record<string, string[]>
}

// One descriptor per possible input. The form renders only the entries named
// by the chosen kind's `fields`, so each kind shows exactly what it uses.
const FIELD_DEFS: Record<string, { label: string; secret?: boolean; placeholder?: string }> = {
  baseUrl: { label: "Base URL", placeholder: "https://abc12345.live.dynatrace.com" },
  subscriptionCode: { label: "Subscription code" },
  clientId: { label: "Client ID" },
  clientSecret: { label: "Client secret", secret: true },
  tokenEndpoint: { label: "Token endpoint" },
  resource: { label: "Resource" },
  username: { label: "Username" },
  password: { label: "Password", secret: true },
  apiToken: { label: "API token", secret: true },
  externalEnvironmentCode: { label: "Environment code" },
}

// The tile per platform. Google Drive is personal (the caller's own account,
// one consent, no form); SAP and Dynatrace are company connections managed
// through the requirements-driven form. `logo` makes the tile — Drive's is
// the inline SVG; Dynatrace and SAP use the brand images in public/logos.
type Tile = {
  id: "google-drive" | "dynatrace" | "sap"
  name: string
  description: string
  logo: (props: { className?: string }) => React.ReactElement
}

const TILES: Tile[] = [
  {
    id: "google-drive",
    name: "Google Drive",
    description: "Your own Drive — read, create and edit files in your agent sessions.",
    logo: GoogleDriveLogo,
  },
  {
    id: "dynatrace",
    name: "Dynatrace",
    description: "Monitor environments — problems, entities and metrics, read-only.",
    logo: (props) => <img src="/logos/dynatrace.png" alt="Dynatrace" {...props} />,
  },
  {
    id: "sap",
    name: "SAP Commerce",
    description: "CCv2 Cloud Portal and HAC — builds, deployments and catalogue data.",
    logo: (props) => <img src="/logos/sap.png" alt="SAP" {...props} />,
  },
]

export function ConnectionsPage() {
  const reqs = useLoad(() => api.get<Requirements>("/connections/requirements"))
  const list = useLoad(() => api.get<{ connections: any[] }>("/connections"))
  const drive = useLoad(() => api.get<{ connections: DriveConnection[] }>("/google-drive/connections"))
  // Which tile's modal is open, if any. Drive never opens one — its connect
  // is the Google redirect.
  const [modalTile, setModalTile] = useState<Tile | null>(null)
  const [editing, setEditing] = useState<any>(null)

  // Company connections grouped by which tile they belong to: sap-* under the
  // SAP tile, dynatrace under Dynatrace.
  const byTile = (id: Tile["id"]) =>
    (list.data?.connections ?? []).filter((c) =>
      id === "sap" ? c.platform?.startsWith("sap") : c.platform === id,
    )

  return (
    <>
      <PageHeader
        title="Connections"
        subtitle="Link the systems your agent can work with in a session — your Drive files, monitoring and SAP environments. Once connected, the agent uses them on your request."
      />

      <DriveOutcomeBanner />
      <ErrorNote error={list.error} />
      <ErrorNote error={drive.error} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TILES.map((tile) => (
          <ConnectionTile
            key={tile.id}
            tile={tile}
            driveConnection={tile.id === "google-drive" ? drive.data?.connections?.[0] : undefined}
            companyConnections={byTile(tile.id)}
            busyDrive={drive.loading || list.loading}
            onAdd={() => {
              setEditing(null)
              setModalTile(tile)
            }}
            onEdit={(c) => {
              setModalTile(tile)
              setEditing(c)
            }}
          />
        ))}
      </div>

      {modalTile && reqs.data && (
        <Modal
          title={editing ? `Edit ${editing.label}` : `Add ${modalTile.name} connection`}
          onClose={() => (setModalTile(null), setEditing(null))}
        >
          <ConnectionForm
            reqs={reqs.data}
            connection={editing ?? undefined}
            // Prefill the platform from the tile: SAP tiles manage SAP
            // connections, the Dynatrace tile Dynatrace — the picker is
            // disabled either way, so the tile decides.
            initialPlatform={modalTile.id === "dynatrace" ? "dynatrace" : "sap-commerce"}
            onDone={() => (setModalTile(null), setEditing(null), list.reload())}
          />
        </Modal>
      )}
    </>
  )
}

function ConnectionTile({
  tile,
  driveConnection,
  companyConnections,
  busyDrive,
  onAdd,
  onEdit,
}: {
  tile: Tile
  driveConnection?: DriveConnection
  companyConnections: any[]
  busyDrive: boolean
  onAdd: () => void
  onEdit: (connection: any) => void
}) {
  const isDrive = tile.id === "google-drive"
  const connected = isDrive ? driveConnection !== undefined : companyConnections.length > 0

  return (
    <Card className="flex flex-col items-start gap-3 p-5">
      <div className="relative">
        <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-border bg-background p-3 shadow-sm">
          <tile.logo className="h-full w-full" />
        </div>
        {/* Status dot: green when something is connected, grey when empty.
            The only state a glance needs. */}
        <span
          className={`absolute -right-1 -top-1 h-4 w-4 rounded-full border-2 border-surface ${
            connected ? "bg-success" : "bg-faint"
          }`}
          title={connected ? "Connected" : "Not connected"}
        />
      </div>
      <div>
        <p className="font-medium text-foreground">{tile.name}</p>
        <p className="text-xs text-muted">{tile.description}</p>
      </div>

      {/* What is connected, briefly. Company connections expand into their own
          edit buttons; Drive names the account. */}
      <div className="w-full">
        {isDrive ? (
          driveConnection && !busyDrive ? (
            <p className="text-xs text-muted">
              {driveConnection.googleAccount}
              {driveConnection.status === "needs_reconnect" && (
                <span className="text-warning"> · needs reconnect</span>
              )}
            </p>
          ) : null
        ) : (
          companyConnections.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 py-0.5">
              <p className="min-w-0 truncate text-xs text-muted">{c.label}</p>
              <Button variant="ghost" className="text-xs" onClick={() => onEdit(c)}>Edit</Button>
            </div>
          ))
        )}
      </div>

      {/* The actions, as buttons rather than the whole card being one — a
          Disconnect must not sit on the same click target as a Connect. */}
      <div className="mt-auto flex w-full flex-wrap gap-2">
        {busyDrive ? (
          <Spinner label="" />
        ) : isDrive ? (
          <>
            <Button onClick={connect}>{connected ? "Reconnect" : "Connect"}</Button>
            {driveConnection && (
              <DisconnectDriveButton connection={driveConnection} />
            )}
          </>
        ) : (
          <>
            <Button onClick={onAdd}>Add connection</Button>
            {companyConnections.length > 0 && <DisconnectCompanyButtons connections={companyConnections} />}
          </>
        )}
      </div>
    </Card>
  )
}

/** Disconnect for the Drive tile: one connection, confirmed, back to empty. */
function DisconnectDriveButton({ connection }: { connection: DriveConnection }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant="danger"
      disabled={busy}
      onClick={async () => {
        if (!confirm(`Disconnect ${connection.googleAccount}? Your agent sessions lose access to this Drive.`)) return
        setBusy(true)
        try {
          await api.delete(`/google-drive/connections/${connection.id}`)
          window.location.reload()
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? "…" : "Disconnect"}
    </Button>
  )
}

/** Disconnect for a company tile: one per connection, so the right one is
 *  removed. A single connection labels itself; two or more say which. */
function DisconnectCompanyButtons({ connections }: { connections: any[] }) {
  const [busyId, setBusyId] = useState<string | null>(null)
  return (
    <>
      {connections.map((c) => (
        <Button
          key={c.id}
          variant="danger"
          disabled={busyId !== null}
          onClick={async () => {
            if (!confirm(`Remove "${c.label}"? The agent loses access to this ${c.platform} environment.`)) return
            setBusyId(c.id)
            try {
              await api.delete(`/connections/${c.id}`)
              window.location.reload()
            } finally {
              setBusyId(null)
            }
          }}
        >
          {connections.length === 1 ? "Disconnect" : `Disconnect ${c.label}`}
        </Button>
      ))}
    </>
  )
}

function ConnectionForm({
  reqs, connection, onDone, initialPlatform,
}: { reqs: Requirements; connection?: any; onDone: () => void; initialPlatform?: string }) {
  const editing = connection !== undefined
  const [platform, setPlatform] = useState(connection?.platform ?? initialPlatform ?? reqs.platforms[0])
  const kinds = reqs.kindsByPlatform[platform] ?? reqs.targetKinds.map((k) => k.targetKind)
  const [kind, setKind] = useState(connection?.targetKind ?? kinds[0])
  const [error, setError] = useState<unknown>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const requirement = reqs.targetKinds.find((k) => k.targetKind === kind)
  const required = new Set([...(requirement?.required ?? []), ...(requirement?.requiredSecrets ?? [])])

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const body: Record<string, unknown> = {}
    // Empty fields are not sent. On create that skips optional fields; on edit
    // it also means a blank secret keeps the stored one — the server only
    // touches secrets that were supplied.
    form.forEach((value, key) => { if (String(value).trim()) body[key] = value })
    setBusy(true); setError(null); setWarnings([])
    try {
      const res = editing
        ? await api.patch<{ warnings?: string[] }>(`/connections/${connection.id}`, body)
        : await api.post<{ warnings?: string[] }>("/connections", body)
      // Surfaced rather than swallowed: a kind nothing validated should say so.
      if (res.warnings?.length) setWarnings(res.warnings)
      else onDone()
    } catch (err) { setError(err) } finally { setBusy(false) }
  }

  // What the stored secret renders as while unchanged — the mask the server
  // sent, so the field shows a credential exists without revealing it.
  const secretHint = (name: string) =>
    editing ? { placeholder: connection.secrets?.[name] ?? "", hint: "Leave blank to keep the stored value." } : {}

  return (
    <Card className="p-5">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {/* A connection cannot move to another platform; disabled inputs are
            also left out of the submitted form data. */}
        <Select
          label="Platform"
          name="platform"
          value={platform}
          disabled={editing}
          onChange={(e) => {
            const next = e.target.value
            setPlatform(next)
            // A kind belongs to one platform — switching platform resets it to
            // the first kind that fits.
            setKind((reqs.kindsByPlatform[next] ?? reqs.targetKinds.map((k) => k.targetKind))[0]!)
          }}
        >
          {reqs.platforms.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
        <Select label="Target kind" name="targetKind" value={kind} onChange={(e) => setKind(e.target.value)}>
          {kinds.map((targetKind) => {
            const k = reqs.targetKinds.find((r) => r.targetKind === targetKind)
            return (
              <option key={targetKind} value={targetKind}>
                {targetKind}{k && !k.implemented ? " (not validated)" : ""}
              </option>
            )
          })}
        </Select>

        {requirement && !requirement.implemented && requirement.note && (
          <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
            {requirement.note}
          </p>
        )}
        {requirement?.either?.length ? (
          <p className="text-xs text-muted">
            Needs {requirement.either.map((g) => `${g.describes} (${g.fields.join(", ")})`).join(" or ")}.
          </p>
        ) : null}

        <Field label="Key" name="key" placeholder="prod" defaultValue={connection?.key} required />
        <Field label="Label" name="label" placeholder="Production CCv2" defaultValue={connection?.label} required />
        <Select label="Access policy" name="accessPolicy" defaultValue={connection?.accessPolicy ?? "READ_ONLY"}>
          <option value="READ_ONLY">Read only</option>
          <option value="WRITES_WITH_APPROVAL">Writes with approval</option>
          <option value="BLOCKED">Blocked</option>
        </Select>

        {/* Only the fields this kind actually uses. Hidden fields are not
            submitted either — changing kind cannot smuggle a stale value in. */}
        {(requirement?.fields ?? []).map((name) => {
          const def = FIELD_DEFS[name]
          if (!def) return null
          return (
            <Field
              key={name}
              name={name}
              label={def.label}
              placeholder={def.placeholder}
              type={def.secret ? "password" : "text"}
              required={required.has(name)}
              defaultValue={def.secret ? undefined : connection?.[name]}
              {...(def.secret ? secretHint(name) : {})}
            />
          )
        })}

        <ErrorNote error={error} />
        {warnings.map((w) => (
          <p key={w} className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
            Saved. {w}
          </p>
        ))}
        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>{busy ? "Saving…" : editing ? "Save changes" : "Save connection"}</Button>
          <Button type="button" variant="ghost" onClick={onDone}>{warnings.length ? "Done" : "Cancel"}</Button>
        </div>
      </form>
    </Card>
  )
}

// ---------- My Google Drive (ROA-8) ----------

type DriveConnection = {
  id: string
  googleAccount: string
  scopes: string[]
  status: "connected" | "needs_reconnect"
  lastUsedAt: string | null
  createdAt: string
}

// The same reason codes the OAuth callback redirects with, mapped to sentences
// a person can act on — shared contract with the space page's CONNECT_ERRORS.
const CONNECT_ERRORS: Record<string, string> = {
  declined: "The Google sign-in was cancelled, so nothing was connected.",
  state_missing: "That sign-in link was not one we started. Try connecting again.",
  state_expired: "The Google sign-in took too long. Try connecting again.",
  no_refresh_token:
    "Google did not return a long-lived token. Remove Moderniser from the account's third-party access at myaccount.google.com and connect again.",
  token_exchange_failed: "Google refused to complete the sign-in. Try again.",
  not_configured: "Google Drive is not configured on this server yet — ask an administrator.",
}

// The OAuth return banner. The callback redirects here with ?googleDrive=…;
// read once and cleared so a refresh does not replay a stale banner.
function DriveOutcomeBanner() {
  const [params, setParams] = useSearchParams()
  const outcome = params.get("googleDrive")
  if (!outcome) return null
  const dismiss = () => {
    const next = new URLSearchParams(params)
    next.delete("googleDrive")
    next.delete("reason")
    setParams(next, { replace: true })
  }
  return (
    <button
      type="button"
      onClick={dismiss}
      className={`rounded-md border px-3 py-2 text-left text-sm ${
        outcome === "connected" ? "border-border text-foreground" : "border-danger text-danger"
      }`}
    >
      {outcome === "connected"
        ? "Google Drive connected. Your agent sessions can now work with your files."
        : (CONNECT_ERRORS[params.get("reason") ?? ""] ?? "Connecting to Google failed.")}
    </button>
  )
}

// A full-page redirect to Google: the URL comes from the server, the browser
// leaves. No name, no options — one employee, one connection, and a second
// consent replaces it (that is the reconnect flow).
async function connect() {
  const { url } = await api.get<{ url: string }>("/google-drive/authorize-url")
  window.location.href = url
}
